import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import withTunnel from './withTunnel';
import TunnelPage from './common/TunnelPage';
import { stepIDs } from 'utils/constants';
import {
    useAddressCheck,
    useCosubscriberIdCheck,
    useSubscriberIdCheck,
    useTunnelNavigation,
    useTunnelPhases,
} from 'utils/hooks/tunnel';

const DirectSubscription = props => {
    const {
        allStepIds,
        allTunnelStepIds,
        codeLoader,
        firstFailedUserStep,
        internalUser,
        isFCPR,
        lastPhaseIndex,
        lastTunnelStepMemo,
        loggedIn,
        phaseMemo,
        rolesLoader,
        roleSubmitted,
        stepId,
        stepLoader,
        submittedPedagogicalStep,
        tsId,
        tunnelSubscription,
    } = props;

    const { phases, isLoading: tunnelListLoader } = useTunnelPhases();
    const firstCustomStepIndex = useMemo(
        () => allStepIds.findIndex(sId => sId === stepIDs.CUSTOM_STEP),
        [allStepIds]
    );

    // investor tunnel specific logic
    const navigateToStep = useTunnelNavigation(
        tsId || (!tsId && stepId === stepIDs.CUSTOM_STEP)
            ? { tsId: tsId || allTunnelStepIds[firstCustomStepIndex] }
            : undefined
    );
    const dataIsLoading = useMemo(
        () => stepLoader || tunnelListLoader || rolesLoader || codeLoader,
        [stepLoader, tunnelListLoader, rolesLoader, codeLoader]
    );

    const addressCheck = useAddressCheck(true);
    const subscriberIDCheck = useSubscriberIdCheck(true);
    const coSubscriberIDCheck = useCosubscriberIdCheck(true);

    // returns true if subscriber shouldn't have access past addres check step
    const weArePastAddressCheck = useMemo(() => {
        const currentStepIndex = allStepIds.findIndex(step => step === stepId);
        const addressCheckStepIndex = allStepIds.findIndex(step => step === stepIDs.ADDRESS_CHECK);
        return currentStepIndex > addressCheckStepIndex && !addressCheck;
    }, [addressCheck, allStepIds, stepId]);

    // returns true if subscriber shouldn't have access past ID check step
    const weArePastIdCheck = useMemo(() => {
        const currentStepIndex = allStepIds.findIndex(step => step === stepId);
        const idCheckStepIndex = allStepIds.findIndex(step => step === stepIDs.ID_CHECK);
        return currentStepIndex > idCheckStepIndex && !subscriberIDCheck;
    }, [subscriberIDCheck, allStepIds, stepId]);

    // returns true if subscriber shouldn't have access past co-subscriber ID check step
    const weArePastCosubscriberIdCheck = useMemo(() => {
        const currentStepIndex = allStepIds.findIndex(step => step === stepId);
        const cosubscriberIdCheckStepIndex = allStepIds.findIndex(
            step => step === stepIDs.CO_SUBSCRIBER_ID_CHECK
        );
        return (
            cosubscriberIdCheckStepIndex &&
            currentStepIndex > cosubscriberIdCheckStepIndex &&
            !coSubscriberIDCheck
        );
    }, [coSubscriberIDCheck, allStepIds, stepId]);

    // this effect should redirect to the last accessible step if any irregularities:
    // - no stepId in URL
    // - stepId not found in list
    // - step with stepId not accessible
    useEffect(() => {
        if (dataIsLoading || !phases.length) return;
        let phaseIndex = phases.findIndex(ph => ph.tunnelSteps.find(ts => ts.step.id === stepId));
        const firstFailedTunnelStepIndex = phaseMemo?.tunnelSteps.findIndex(
            ts => ts.id === firstFailedUserStep?.tunnelStep.id
        );
        const lastPhase = phases[lastPhaseIndex];
        // current tunnelStep index by stepId from the URL
        const currentTunnelStepIndex = phaseMemo?.tunnelSteps.findIndex(ts =>
            tsId ? ts.id === tsId : ts.step.id === stepId
        );
        const lastTunnelStepIdx = lastPhase?.tunnelSteps.findIndex(
            ts => ts.id === lastTunnelStepMemo?.id
        );
        let targetStepId = lastTunnelStepMemo?.step.id || stepId;
        targetStepId = !targetStepId ? lastTunnelStepMemo?.step?.id : targetStepId;

        if (stepId === stepIDs.CUSTOM_STEP && !tsId) {
            // reset URL params
            navigateToStep(stepId);
        } else if (weArePastAddressCheck) {
            // check that all subscription documents were uploaded
            navigateToStep(stepIDs.ADDRESS_CHECK);
        } else if (weArePastIdCheck) {
            navigateToStep(stepIDs.ID_CHECK);
        } else if (weArePastCosubscriberIdCheck) {
            navigateToStep(stepIDs.CO_SUBSCRIBER_ID_CHECK);
        } else if (
            // continue with the rest of the logic
            loggedIn &&
            (!stepId || // there's no stepId in the URL
                phaseIndex === -1 || // phases are not loaded
                phaseIndex > lastPhaseIndex || // we are past the last accessible tunnel phase
                (currentTunnelStepIndex > lastTunnelStepIdx &&
                    lastTunnelStepIdx > -1 &&
                    phaseIndex === lastPhaseIndex) || // we're past the last accessible step
                (currentTunnelStepIndex > firstFailedTunnelStepIndex &&
                    firstFailedTunnelStepIndex > -1 &&
                    currentTunnelStepIndex > -1)) // there's a failed step blocking
        ) {
            navigateToStep(targetStepId);
        } else if (stepId && !allStepIds.includes(stepId)) {
            navigateToStep(targetStepId);
        } else if (
            !loggedIn &&
            roleSubmitted &&
            phaseMemo &&
            (isFCPR ? submittedPedagogicalStep : true) &&
            !phaseMemo.tunnelSteps.find(ts => ts.step.id === stepId)
        ) {
            navigateToStep(phases[0].tunnelSteps[0].step.id);
        }
    }, [
        dataIsLoading,
        allStepIds,
        phases,
        navigateToStep,
        lastPhaseIndex,
        stepId,
        roleSubmitted,
        phaseMemo,
        internalUser,
        loggedIn,
        isFCPR,
        submittedPedagogicalStep,
        lastTunnelStepMemo,
        firstFailedUserStep,
        tunnelSubscription,
        tsId,
        weArePastAddressCheck,
        weArePastIdCheck,
        weArePastCosubscriberIdCheck,
    ]);

    return <TunnelPage {...props} />;
};

DirectSubscription.propTypes = {
    allStepIds: PropTypes.array,
    allTunnelStepIds: PropTypes.array,
    codeLoader: PropTypes.bool,
    firstFailedUserStep: PropTypes.object,
    internalUser: PropTypes.object,
    isFCPR: PropTypes.bool,
    lastPhaseIndex: PropTypes.number,
    lastTunnelStepMemo: PropTypes.object,
    loggedIn: PropTypes.bool,
    phaseMemo: PropTypes.object,
    rolesLoader: PropTypes.bool,
    roleSubmitted: PropTypes.bool,
    stepId: PropTypes.number,
    stepLoader: PropTypes.bool,
    submittedPedagogicalStep: PropTypes.bool,
    tsId: PropTypes.number,
    tunnelSubscription: PropTypes.object,
};

export default withTunnel(DirectSubscription);
